import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import EmailPreviewEditModal from './EmailPreviewEditModal';
import {
  PERMISSION_GROUPS,
  PERMISSION_KEYS,
  roleLabel,
  canManageUserRole,
  assignableRoles
} from '../utils/permissions';

// Permissions a brand new restricted user starts with (mirrors the backend default).
const DEFAULT_NEW_USER_PERMISSIONS = [
  'grants', 'legal_documents', 'board_minutes', 'data_room', 'company_info', 'registrations'
];

const CompanyUserManagement = ({ userInfo, companyName }) => {
  const [users, setUsers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('user');
  const [newUserPermissions, setNewUserPermissions] = useState(DEFAULT_NEW_USER_PERMISSIONS);
  // The access checklist is collapsed until the inviter wants to change it.
  const [showNewUserPermissions, setShowNewUserPermissions] = useState(false);
  // Role of the logged-in user within this company ('founder' | 'super_user' |
  // 'user'), or their admin account type. Drives what they're allowed to manage.
  const [currentUserRole, setCurrentUserRole] = useState(null);
  // Per-user access editing
  const [editingPermissionsFor, setEditingPermissionsFor] = useState(null);
  const [permissionDraft, setPermissionDraft] = useState([]);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editUserData, setEditUserData] = useState({ name: '', email: '', role: '' });
  const [originalUserEmail, setOriginalUserEmail] = useState('');
  const [changingUsername, setChangingUsername] = useState(null);
  const [newUsername, setNewUsername] = useState('');

  // Email preview/edit modal state
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailVariables, setEmailVariables] = useState({});
  const [emailRecipient, setEmailRecipient] = useState('');
  const [pendingInvitationData, setPendingInvitationData] = useState(null);

  useEffect(() => {
    if (companyName) {
      fetchCompanyUsers();
    }
  }, [companyName]);

  const fetchCompanyUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/auth/company/${encodeURIComponent(companyName)}/users`
      );
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || data); // Handle both old and new API format
        setPendingInvitations(data.pendingInvitations || []);
        setCurrentUserRole(
          data.currentUserRole ||
          (userInfo?.accountType === 'admin' || userInfo?.accountType === 'superadmin'
            ? userInfo.accountType
            : userInfo?.companyRole || (userInfo?.isSuperUser ? 'super_user' : 'user'))
        );
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch company users');
      }
    } catch (error) {
      console.error('Error fetching company users:', error);
      setError('Network error occurred');
    }
    setLoading(false);
  };

  const handleToggleAlerts = async (userEmail, receiveAlerts) => {
    try {
      const response = await api.put(
        `${process.env.REACT_APP_BACKEND_API_URL}/auth/company/${encodeURIComponent(companyName)}/users/${encodeURIComponent(userEmail)}/alerts`,
        { receiveAlerts }
      );

      if (response.ok) {
        // Update local state
        setUsers(users.map(u =>
          u.userEmail === userEmail ? { ...u, receiveAlerts } : u
        ));
        setSuccess('Alert settings updated successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to update alert settings');
        setTimeout(() => setError(''), 5000);
      }
    } catch (error) {
      console.error('Error updating alert settings:', error);
      setError('Failed to update alert settings');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUserEmail.trim()) return;

    setError('');
    setSuccess('');
    setAddingUser(true);

    try {
      // Call backend with skipEmail: true to create invitation without sending email
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/auth/company/${encodeURIComponent(companyName)}/users`,
        {
          userEmail: newUserEmail.trim(),
          role: newUserRole,
          permissions: newUserRole === 'user' ? newUserPermissions : null,
          skipEmail: true
        }
      );

      const data = await response.json();

      if (response.ok) {
        // Prepare email variables for the preview modal
        const roleText = newUserRole === 'super_user' ? 'Super User' :
                        newUserRole === 'founder' ? 'Founder' : 'User';

        setEmailVariables({
          companyName: companyName,
          addedByName: data.addedByName || userInfo?.name || userInfo?.email || 'Administrator',
          roleText: roleText,
          registrationUrl: data.registrationUrl,
          email: newUserEmail.trim()
        });
        setEmailRecipient(newUserEmail.trim());

        // Store invitation data for later
        setPendingInvitationData({
          userEmail: newUserEmail.trim(),
          role: newUserRole
        });

        // Show email preview modal
        setShowEmailPreview(true);
      } else {
        setError(data.error || 'Failed to prepare user invitation');
      }
    } catch (error) {
      console.error('Error preparing user invitation:', error);
      setError('Network error occurred');
    }

    setAddingUser(false);
  };

  const handleSendUserInvitation = async (emailData) => {
    try {
      // Send the email via /emails/send endpoint
      const emailResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/emails/send`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: emailData.recipientEmail,
          subject: emailData.subject,
          html: emailData.htmlContent,
          emailType: 'User Invitation'
        })
      });

      if (emailResponse.ok) {
        setSuccess(`User ${pendingInvitationData.userEmail} added and invitation sent successfully!`);
        setNewUserEmail('');
        setNewUserRole('user');
        setNewUserPermissions(DEFAULT_NEW_USER_PERMISSIONS);
        setShowNewUserPermissions(false);
        setShowEmailPreview(false);
        setPendingInvitationData(null);
        fetchCompanyUsers(); // Refresh the list
      } else {
        const errorData = await emailResponse.json();
        setError(errorData.error || 'Failed to send invitation email');
      }
    } catch (error) {
      console.error('Error sending invitation email:', error);
      setError('Network error occurred while sending email');
    }
  };

  // Hierarchy helpers -------------------------------------------------------
  const isAdminAccount = userInfo?.accountType === 'admin' || userInfo?.accountType === 'superadmin';
  const allowedRoles = assignableRoles(currentUserRole, userInfo?.accountType);
  const canManage = (targetRole) => canManageUserRole(currentUserRole, targetRole, userInfo?.accountType);

  const togglePermission = (list, key) =>
    list.includes(key) ? list.filter(k => k !== key) : [...list, key];

  const handleEditPermissions = (user) => {
    setEditingPermissionsFor(user);
    setPermissionDraft(Array.isArray(user.permissions) ? [...user.permissions] : [...PERMISSION_KEYS]);
  };

  const handleSavePermissions = async () => {
    if (!editingPermissionsFor) return;
    setSavingPermissions(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.put(
        `${process.env.REACT_APP_BACKEND_API_URL}/auth/company/${encodeURIComponent(companyName)}/users/${encodeURIComponent(editingPermissionsFor.userEmail)}/permissions`,
        { permissions: permissionDraft }
      );

      if (response.ok) {
        const data = await response.json();
        setUsers(users.map(u =>
          u.userEmail === editingPermissionsFor.userEmail
            ? { ...u, permissions: data.permissions || permissionDraft }
            : u
        ));
        setSuccess(`Access updated for ${editingPermissionsFor.userEmail}`);
        setTimeout(() => setSuccess(''), 3000);
        setEditingPermissionsFor(null);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to update user access');
      }
    } catch (error) {
      console.error('Error updating user permissions:', error);
      setError('Failed to update user access');
    }
    setSavingPermissions(false);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setOriginalUserEmail(user.userEmail);
    setEditUserData({
      name: user.userName || user.userEmail,
      email: user.userEmail,
      role: user.role || 'user'
    });
  };

  const handleSaveUser = async () => {
    if (!editUserData.name.trim()) {
      setError('Name cannot be empty');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editUserData.email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    const emailChanged = originalUserEmail !== editUserData.email.trim();
    const roleChanged = editingUser.role !== editUserData.role;

    try {
      if (emailChanged) {
        // Email has changed - initiate username change process
        const response = await api.post(
          `${process.env.REACT_APP_BACKEND_API_URL}/auth/change-username`,
          {
            targetUsername: originalUserEmail,
            newUsername: editUserData.email.trim()
          }
        );

        if (response.ok) {
          setSuccess('Email verification process initiated. Verification emails have been sent to both the old and new email addresses.');
          setEditingUser(null);
          setEditUserData({ name: '', email: '', role: '' });
          setOriginalUserEmail('');
          fetchCompanyUsers(); // Refresh the list
        } else {
          const data = await response.json();
          setError(data.error || 'Failed to initiate email change');
        }
      } else {
        // Handle name and/or role changes
        let nameUpdateSuccess = true;
        let roleUpdateSuccess = true;

        // Update name if changed
        if (editingUser.userName !== editUserData.name.trim()) {
          const nameResponse = await api.put(
            `${process.env.REACT_APP_BACKEND_API_URL}/auth/company/${encodeURIComponent(companyName)}/users/${encodeURIComponent(editUserData.email)}/name`,
            {
              userEmail: editUserData.email,
              newName: editUserData.name.trim()
            }
          );

          if (!nameResponse.ok) {
            const data = await nameResponse.json();
            setError(data.error || 'Failed to update user name');
            nameUpdateSuccess = false;
          }
        }

        // Update role if changed
        if (roleChanged && nameUpdateSuccess) {
          const roleResponse = await api.put(
            `${process.env.REACT_APP_BACKEND_API_URL}/auth/company/${encodeURIComponent(companyName)}/users/${encodeURIComponent(editUserData.email)}/role`,
            {
              userEmail: editUserData.email,
              newRole: editUserData.role
            }
          );

          if (!roleResponse.ok) {
            const data = await roleResponse.json();
            setError(data.error || 'Failed to update user role');
            roleUpdateSuccess = false;
          }
        }

        if (nameUpdateSuccess && roleUpdateSuccess) {
          setSuccess('User updated successfully!');
          setEditingUser(null);
          setEditUserData({ name: '', email: '', role: '' });
          setOriginalUserEmail('');
          fetchCompanyUsers(); // Refresh the list
        }
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setError('Failed to update user');
    }
  };

  const handleCancelEditUser = () => {
    setEditingUser(null);
    setEditUserData({ name: '', email: '', role: '' });
    setOriginalUserEmail('');
  };

  const handleChangeUsername = (userEmail) => {
    setChangingUsername(userEmail);
    setNewUsername('');
  };

  const handleSaveUsername = async () => {
    if (!newUsername.trim()) {
      setError('New username cannot be empty');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUsername.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setError('');
    setSuccess('');

    try {
      console.log('Username change request:', {
        targetUsername: changingUsername,
        newUsername: newUsername.trim()
      });

      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/auth/change-username`,
        {
          targetUsername: changingUsername,
          newUsername: newUsername.trim()
        }
      );

      if (response.ok) {
        setSuccess(`Username change request sent! The user will receive verification emails to complete the change.`);
        setChangingUsername(null);
        setNewUsername('');
        fetchCompanyUsers(); // Refresh the list
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to initiate username change');
      }
    } catch (error) {
      console.error('Error changing username:', error);
      setError('Failed to change username');
    }
  };

  const handleCancelUsernameChange = () => {
    setChangingUsername(null);
    setNewUsername('');
  };

  const handleResendInvitation = async (email) => {
    try {
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/auth/company/${encodeURIComponent(companyName)}/resend-client-invitation`,
        { email }
      );

      if (response.ok) {
        setSuccess(`Invitation resent to ${email} successfully!`);
        fetchCompanyUsers(); // Refresh the list
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to resend invitation');
      }
    } catch (error) {
      console.error('Error resending invitation:', error);
      setError('Network error occurred');
    }
  };

  const handleDeleteInvitation = async (email) => {
    if (!window.confirm(`Are you sure you want to delete the pending invitation for ${email}?`)) {
      return;
    }

    try {
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/auth/company/${encodeURIComponent(companyName)}/delete-client-invitation`,
        { email }
      );

      if (response.ok) {
        setSuccess(`Invitation for ${email} deleted successfully!`);
        fetchCompanyUsers(); // Refresh the list
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete invitation');
      }
    } catch (error) {
      console.error('Error deleting invitation:', error);
      setError('Network error occurred');
    }
  };

  const handleRemoveUser = async (userEmail, userRole) => {
    const isCurrentUser = userEmail === userInfo?.email;
    const isSuperUser = userRole === 'super_user';
    
    // Prevent super clients from removing themselves
    if (isCurrentUser && userInfo?.isSuperUser) {
      setError('You cannot remove yourself from the user list.');
      return;
    }
    
    // For super clients, warn about removing super client users
    if (userInfo?.isSuperUser && isSuperUser && userEmail !== userInfo?.email) {
      if (!window.confirm(`WARNING: You are about to remove another Super User (${userEmail}) from the authorized user list. This will revoke their admin privileges for this company. Are you sure you want to continue?`)) {
        return;
      }
    } else {
      if (!window.confirm(`Are you sure you want to remove ${userEmail} from the authorized user list?`)) {
        return;
      }
    }

    try {
      const response = await api.delete(
        `${process.env.REACT_APP_BACKEND_API_URL}/auth/company/${encodeURIComponent(companyName)}/users/${encodeURIComponent(userEmail)}`
      );

      const data = await response.json();
      
      if (response.ok) {
        setSuccess(`User ${userEmail} removed successfully!`);
        fetchCompanyUsers(); // Refresh the list
      } else {
        setError(data.error || 'Failed to remove user');
      }
    } catch (error) {
      console.error('Error removing user:', error);
      setError('Network error occurred');
    }
  };

  if (!companyName) {
    return (
      <div className="card p-6">
        <p className="text-gray-500">No company selected for user management.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          User List Management - {companyName}
        </h3>
        <p className="text-gray-600">
          {userInfo?.isSuperUser 
            ? 'Manage the list of users who are authorized to register for your company. Only users on this list (or the super client) can create accounts.'
            : 'Manage the list of users who are authorized to register for this company. You can add or remove users from the authorized list.'
          }
        </p>
        <div className="mt-3 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1">
          <p><span className="font-medium text-gray-900">Founders</span> and <span className="font-medium text-gray-900">Super Users</span> have full access. Founders can manage all users; Super Users can manage all users except Founders.</p>
          <p><span className="font-medium text-gray-900">Users</span> only see the sections checked for them below, and each user's access is set individually.</p>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-600">{success}</p>
        </div>
      )}

      {/* Add User Form */}
      {allowedRoles.length > 0 && (
        <div className="card p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Add New User</h4>
          <form onSubmit={handleAddUser} className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  className="input-field"
                  placeholder="Enter user email address"
                  required
                />
              </div>
              <div className="w-36">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  value={newUserRole}
                  onChange={(e) => setNewUserRole(e.target.value)}
                  className="input-field"
                >
                  {allowedRoles.map(role => (
                    <option key={role} value={role}>{roleLabel(role)}</option>
                  ))}
                </select>
              </div>
              <div className="flex pl-5 items-end">
                <button
                  type="submit"
                  disabled={addingUser}
                  className={`btn-primary ${addingUser ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {addingUser ? 'Adding...' : 'Add User'}
                </button>
              </div>
            </div>

            {/* Access checkboxes - only restricted users have a limited view.
                Collapsed by default so the form stays compact; the summary line
                still shows what the new user would get. */}
            {newUserRole === 'user' ? (
              <div className="border border-gray-200 rounded-lg bg-gray-50">
                <button
                  type="button"
                  onClick={() => setShowNewUserPermissions(open => !open)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left"
                >
                  <span className="text-sm font-medium text-gray-700">
                    Access
                    <span className="ml-2 font-normal text-gray-500">
                      {newUserPermissions.length} of {PERMISSION_KEYS.length} sections
                    </span>
                  </span>
                  <span className="flex items-center text-xs text-blue-600">
                    {showNewUserPermissions ? 'Hide' : 'Customize'}
                    <svg
                      className={`w-4 h-4 ml-1 transition-transform duration-200 ${showNewUserPermissions ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </button>
              {showNewUserPermissions && (
              <div className="px-4 pb-4 border-t border-gray-200 pt-3">
                <div className="flex items-center justify-end space-x-3 text-xs mb-3">
                  <button
                    type="button"
                    onClick={() => setNewUserPermissions(PERMISSION_KEYS)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewUserPermissions([])}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Clear all
                  </button>
                </div>
                {PERMISSION_GROUPS.map(group => (
                  <div key={group.title} className="mb-3 last:mb-0">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{group.title}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {group.permissions.map(permission => (
                        <label key={permission.key} className="flex items-center space-x-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={newUserPermissions.includes(permission.key)}
                            onChange={() => setNewUserPermissions(togglePermission(newUserPermissions, permission.key))}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span>{permission.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                {roleLabel(newUserRole)}s have full access to every section.
              </p>
            )}
          </form>
        </div>
      )}

      {/* User List */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h4 className="text-lg font-medium text-gray-900">Authorized Users</h4>
          <p className="text-sm text-gray-600 mt-1">
            Users who can register for {companyName}
          </p>
        </div>
        
        {/* Pending Invitations Section */}
        {pendingInvitations.length > 0 && (
          <div className="px-6 py-4 border-b border-gray-200 bg-yellow-50">
            <h5 className="text-md font-medium text-gray-900 mb-3">Pending Invitations ({pendingInvitations.length})</h5>
            <div className="space-y-2">
              {pendingInvitations.map((invitation, index) => (
                <div key={index} className="flex items-center justify-between bg-white p-3 rounded border">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className="font-medium text-gray-900">{invitation.name || invitation.email}</span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        invitation.role === 'super_user' 
                          ? 'bg-blue-100 text-blue-800' 
                          : invitation.role === 'founder'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {invitation.role === 'super_user' ? 'Super User' : 
                         invitation.role === 'founder' ? 'Founder' : 'User'}
                      </span>
                      <span className="text-sm text-gray-500">
                        Invited {new Date(invitation.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleResendInvitation(invitation.email)}
                      className="px-3 py-1 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors duration-200"
                    >
                      Resend
                    </button>
                    <button
                      onClick={() => handleDeleteInvitation(invitation.email)}
                      className="px-3 py-1 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors duration-200"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-gray-500">No authorized users found. Add users to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Added Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Alerts & Notifications
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Access
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {user.userName || user.userEmail}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={() => {
                          const subject = encodeURIComponent('Message from StreamLaunch Administrator');
                          const body = encodeURIComponent(`Hello ${user.userName || user.userEmail},\n\nI hope this message finds you well.\n\nBest regards,\n${userInfo?.name || userInfo?.email || 'Administrator'}`);
                          window.open(`mailto:${user.userEmail}?subject=${subject}&body=${body}`, '_blank');
                        }}
                        className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                        title="Click to email this user"
                      >
                        {user.userEmail}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.role === 'super_user'
                            ? 'bg-blue-100 text-blue-800'
                            : user.role === 'founder'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.role === 'super_user' ? 'Super User' :
                           user.role === 'founder' ? 'Founder' : 'User'}
                        </span>
                        {user.role === 'founder' && user.personalInfoSubmitted === 'false' && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Info Pending
                          </span>
                        )}
                        {user.role === 'founder' && user.personalInfoSubmitted === 'true' && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Info Submitted
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.addedAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={user.receiveAlerts !== false}
                        disabled={!canManage(user.role)}
                        onChange={(e) => handleToggleAlerts(user.userEmail, e.target.checked)}
                        className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${canManage(user.role) ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                        title="Enable/disable alerts and notifications for this user"
                      />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {user.role === 'user' ? (
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-600">
                            {(user.permissions || []).length} of {PERMISSION_KEYS.length} sections
                          </span>
                          {canManage(user.role) && (
                            <button
                              onClick={() => handleEditPermissions(user)}
                              className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 rounded transition-colors duration-200"
                              title="Choose which sections this user can access"
                            >
                              Edit Access
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500">Full access</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const isCurrentUser = user.userEmail === userInfo?.email;
                        // Founders manage everyone; Super Users manage everyone
                        // except Founders; restricted users manage nobody.
                        const manageable = canManage(user.role);
                        const canRemove = manageable && !isCurrentUser;
                        
                        return (
                          <div className="flex space-x-2">
                            {manageable && (
                              <button
                                onClick={() => handleEditUser(user)}
                                className="px-3 py-1 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors duration-200"
                              >
                                Edit
                              </button>
                            )}
                            {isAdminAccount && (
                              <button
                                onClick={() => handleChangeUsername(user.userEmail)}
                                className="px-3 py-1 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors duration-200"
                                title="Change user's email address"
                              >
                                Change Email
                              </button>
                            )}
                            {canRemove && (
                              <button 
                                onClick={() => handleRemoveUser(user.userEmail, user.role)}
                                className="px-3 py-1 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors duration-200"
                                title={user.role === 'super_user' ? 'Remove Super User (will revoke admin privileges)' : 'Remove user from authorized list'}
                              >
                                Remove
                              </button>
                            )}
                            {isCurrentUser && <span className="text-xs text-gray-400 ml-2">You</span>}
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-[32rem] shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Edit User: {editUserData.email}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={editUserData.name}
                    onChange={(e) => setEditUserData({ ...editUserData, name: e.target.value })}
                    className="input-field w-full"
                    placeholder="Enter user full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={editUserData.email}
                    onChange={(e) => setEditUserData({...editUserData, email: e.target.value})}
                    className="input-field w-full"
                    placeholder="Enter new company email address"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Changing the email will send verification emails to both the old and new addresses
                  </p>
                </div>
                {canManage(editingUser?.role) && allowedRoles.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      User Role
                    </label>
                    <select
                      value={editUserData.role}
                      onChange={(e) => setEditUserData({...editUserData, role: e.target.value})}
                      className="input-field w-full"
                    >
                      {/* Keep the current role selectable even if this manager
                          couldn't otherwise assign it. */}
                      {Array.from(new Set([...allowedRoles, editingUser?.role].filter(Boolean))).map(role => (
                        <option key={role} value={role}>{roleLabel(role)}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Founders and Super Users have full access. Founders can manage all users;
                      Super Users can manage all users except Founders.
                    </p>
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={handleCancelEditUser}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveUser}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Username Modal */}
      {changingUsername && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-[32rem] shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Change Email Address
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Changing the email address for: <strong>{changingUsername}</strong>
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                <div className="text-sm text-yellow-800">
                  <strong>Important:</strong> The user will receive verification emails at both their current personal email
                  and the new company email. They must verify both to complete the change. After verification,
                  they will log in using the new email address.
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="newUsername" className="block text-sm font-medium text-gray-700 mb-2">
                  New Email Address (Company Email)
                </label>
                <input
                  id="newUsername"
                  type="email"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="user@company.com"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelUsernameChange}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveUsername}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                >
                  Send Verification Emails
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Access (permissions) Modal */}
      {editingPermissionsFor && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-[36rem] max-w-[95vw] shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              Edit Access
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {editingPermissionsFor.userName || editingPermissionsFor.userEmail} ({editingPermissionsFor.userEmail})
            </p>

            <div className="flex justify-end space-x-3 text-xs mb-3">
              <button
                type="button"
                onClick={() => setPermissionDraft(PERMISSION_KEYS)}
                className="text-blue-600 hover:text-blue-800"
              >
                Select all
              </button>
              <button
                type="button"
                onClick={() => setPermissionDraft([])}
                className="text-blue-600 hover:text-blue-800"
              >
                Clear all
              </button>
            </div>

            <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-1">
              {PERMISSION_GROUPS.map(group => (
                <div key={group.title}>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{group.title}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {group.permissions.map(permission => (
                      <label key={permission.key} className="flex items-center space-x-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={permissionDraft.includes(permission.key)}
                          onChange={() => setPermissionDraft(togglePermission(permissionDraft, permission.key))}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span>{permission.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setEditingPermissionsFor(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePermissions}
                disabled={savingPermissions}
                className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 ${savingPermissions ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {savingPermissions ? 'Saving...' : 'Save Access'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Preview/Edit Modal */}
      <EmailPreviewEditModal
        isOpen={showEmailPreview}
        onClose={() => {
          setShowEmailPreview(false);
          setPendingInvitationData(null);
        }}
        onSend={handleSendUserInvitation}
        templateKey="client_invitation"
        variables={emailVariables}
        recipientEmail={emailRecipient}
        title="User Invitation Email"
        loading={false}
      />
    </div>
  );
};

export default CompanyUserManagement;